import React from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Container from '@mui/material/Container'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import InventoryForm from '../components/inventory/inventoryForm'
import { useContextHook } from '../store/context'
import { Inventory } from '../types/inventory'
import { get, patch, post, put, setPageTitle } from '../utils/service'
import Alert from '../components/alert/alert'
import { weightValuesToDisplayValues, weightValuesToSaveValues } from '../utils/inventoryUtils'
import { toReadableTimestamp } from '../utils/dateUtils'
import { CuppingSession } from '../types/cuppingSession'
import { getLogName, getLogsUrl } from '../utils/logUtils'
import { Log } from '../types/log'
import { PagedResponse } from '../types/pagedResponse'
import CuppingLinkButton from '../components/cupping/cuppingLinkButton'
import { LEFT_COLUMN_WIDTH } from '../utils/theme'
import LinkCard from '../components/fields/linkCard'

const InventoryDetailView = (): JSX.Element => {
  const { state } = useContextHook()
  const { t } = useTranslation()
  const theme = useTheme()
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const locationState = location.state as { inventoryData: Inventory }

  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [beanInfo, setBeanInfo] = React.useState<Inventory | null>(null)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [hasSaveError, setHasSaveError] = React.useState<boolean>(false)
  const [saveSuccessMessage, setSaveSuccessMessage] = React.useState<string>('')

  const [isSettingArchived, setIsSettingArchived] = React.useState<boolean>(false)
  const [archiveErrorMessage, setArchiveErrorMessage] = React.useState<string>('')
  const [archiveSuccessMessage, setArchiveSuccessMessage] = React.useState<string>('')

  const [isLoadingRecentLogs, setIsLoadingRecentLogs] = React.useState<boolean>(false)
  const [hasLoadRecentLogsError, setHasLoadRecentLogsError] = React.useState<boolean>(false)
  const [recentLogs, setRecentLogs] = React.useState<Array<Log> | null>(null)

  const [isLoadingRecentCuppingSessions, setIsLoadingRecentCuppingSessions] = React.useState<boolean>(false)
  const [hasLoadRecentCuppingSessionsError, setHasLoadRecentCuppingSessionsError] = React.useState<boolean>(false)
  const [recentCuppingSessions, setRecentCuppingSessions] = React.useState<Array<CuppingSession> | null>(null)

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  // update page title
  React.useEffect(() => {
    const pageTitle = beanInfo?.url ? `${t('inventory.editinventory')} ${beanInfo.name}` : t('inventory.createnew')

    setPageTitle(pageTitle)
  }, [beanInfo?.url, beanInfo?.name, t])

  React.useEffect(() => {
    if (state.user && !beanInfo?.id) {
      if (params.inventoryId) {
        setIsLoading(true)
        get<Inventory>(`/inventories/${params.inventoryId}/`)
          .then((response) => {
            setIsLoading(false)
            setBeanInfo(weightValuesToDisplayValues(response.data))
          })
          .catch(() => {
            setHasLoadError(true)
            setIsLoading(false)
          })
      } else if (locationState?.inventoryData) {
        setBeanInfo(locationState.inventoryData)
      } else {
        setBeanInfo({
          id: -1,
          url: '',
          name: '',
          cultivar: '',
          farm: '',
          region: '',
          country: '',
          exporter: '',
          importer: '',
          producer: '',
          elevation: 0,
          moisture: 0,
          water_activity: 0,
          density: 0,
          green_bean_color: 0,
          quality: 0,
          reg_date: null,
          current_weight: 0,
          initial_weight: 0,
          price: 0,
          notes: '',
          is_archived: false,
          bean_process: 0,
          bean_size: 0,
          drying_speed: 0,
          customer: '',
        })
      }
    }
  }, [state.user, params.inventoryId, locationState?.inventoryData, beanInfo?.id])

  React.useEffect(() => {
    if ((beanInfo?.id || 0) > 0 && beanInfo?.customer_id) {
      // load 5 most recent logs with this bean
      setIsLoadingRecentLogs(true)
      get<PagedResponse<Log>>(`${getLogsUrl()}&inventory=${beanInfo.id}&page_size=5`)
        .then((response) => {
          setIsLoadingRecentLogs(false)
          setRecentLogs(response.data.results)
        })
        .catch(() => {
          setIsLoadingRecentLogs(false)
          setHasLoadRecentLogsError(true)
        })

      // load 5 most recent cupping session where this bean is a sample
      setIsLoadingRecentCuppingSessions(true)
      get<PagedResponse<CuppingSession>>(
        `cuppingsessions/?customer=${beanInfo?.customer_id}&inventory=${beanInfo.id}&page_size=5`
      )
        .then((response) => {
          setIsLoadingRecentCuppingSessions(false)
          setRecentCuppingSessions(response.data.results)
        })
        .catch(() => {
          setIsLoadingRecentCuppingSessions(false)
          setHasLoadRecentCuppingSessionsError(true)
        })
    }
  }, [beanInfo?.id, beanInfo?.customer_id])

  const setArchived = (isArchived: boolean): void => {
    if (beanInfo) {
      setIsSettingArchived(true)
      setArchiveErrorMessage('')
      setHasSaveError(false)
      setSaveSuccessMessage('')

      const saveData = {
        is_archived: isArchived,
      }
      patch<{ is_archived: boolean }, Inventory>(beanInfo.url, saveData)
        .then((response) => {
          setBeanInfo(weightValuesToDisplayValues(response.data))
          setArchiveSuccessMessage(isArchived ? 'inventory.archivesuccess' : 'inventory.unarchivesuccess')
          setIsSettingArchived(false)
        })
        .catch(() => {
          setArchiveErrorMessage(isArchived ? 'inventory.archiveerror' : 'inventory.unarchiveerror')
          setIsSettingArchived(false)
        })
    }
  }

  const saveInventory = (inventoryToSave: Inventory): void => {
    setIsSaving(true)
    setSaveSuccessMessage('')
    setArchiveErrorMessage('')
    setHasSaveError(false)
    setArchiveSuccessMessage('')
    const inventoryToSaveInKg = weightValuesToSaveValues(inventoryToSave)
    inventoryToSaveInKg.customer = state.currentCustomer?.url || ''

    const savePromise = inventoryToSaveInKg.url
      ? put<Inventory, Inventory>(inventoryToSaveInKg.url, inventoryToSaveInKg)
      : post<Inventory, Inventory>('inventories/', inventoryToSaveInKg)

    savePromise
      .then((saveInventorySuccessData) => {
        setBeanInfo(weightValuesToDisplayValues(saveInventorySuccessData.data))
        setSaveSuccessMessage(inventoryToSaveInKg.url ? 'inventory.savesuccess' : 'inventory.savesuccessnew')
        setIsSaving(false)

        if (!inventoryToSaveInKg.url) {
          navigate(`/inventories/${saveInventorySuccessData.data.id}`, { replace: true })
        }
      })
      .catch(() => {
        setHasSaveError(true)
        setIsSaving(false)
      })
  }

  return (
    <Container component="main" maxWidth={false}>
      {isLoading && <CircularProgress />}
      {hasSaveError && <Alert severity="error">{t('inventory.saveerror')}</Alert>}
      {saveSuccessMessage && <Alert severity="success">{t(saveSuccessMessage)}</Alert>}
      {hasLoadError && <Alert severity="error">{t('inventory.detailloaderror')}</Alert>}
      {archiveErrorMessage && <Alert severity="error">{t(archiveErrorMessage)}</Alert>}
      {archiveSuccessMessage && <Alert severity="success">{t(archiveSuccessMessage)}</Alert>}
      {beanInfo?.is_archived && !archiveSuccessMessage && (
        <Alert severity="info">{t('inventory.isarchivedinventory')}</Alert>
      )}
      {state.user && beanInfo && (
        <Stack direction={isScreenBelowMdBreakpoint ? 'column-reverse' : 'row'} spacing={5}>
          <Stack width={isScreenBelowMdBreakpoint ? '100%' : LEFT_COLUMN_WIDTH} spacing={5}>
            <Stack>
              <Typography sx={{ fontWeight: 'bold', fontSize: '16px', color: 'primary.main', pb: 2 }}>
                {t('inventory.recentlogs')}
              </Typography>
              {isLoadingRecentLogs && <CircularProgress />}
              {hasLoadRecentLogsError && <Alert severity="error">{t('inventory.recentlogserror')}</Alert>}
              {recentLogs && recentLogs.length > 0 && (
                <Stack spacing={1}>
                  {recentLogs.map((x) => {
                    return (
                      <LinkCard key={x.id} url={`/machine/${x.machine_slug}/logs/${x.id}/`}>
                        <Stack>
                          <Typography variant="body2">{getLogName(x, '', t('logview.batch'))}</Typography>
                          <Typography variant="body2">
                            {x.profile_name || x.profile_data?.name || t('logsview.unknownprofile')}
                          </Typography>
                          <Typography variant="body2">{toReadableTimestamp(x.start_timestamp)}</Typography>
                        </Stack>
                      </LinkCard>
                    )
                  })}
                </Stack>
              )}
              {(beanInfo.id === -1 || recentLogs?.length === 0) && (
                <Alert severity="info">{t('inventory.nologs')}</Alert>
              )}
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 'bold', fontSize: '16px', color: 'primary.main', pb: 2 }}>
                {t('inventory.recentcuppings')}
              </Typography>
              {isLoadingRecentCuppingSessions && <CircularProgress />}
              {hasLoadRecentCuppingSessionsError && (
                <Alert severity="error">{t('inventory.recentcuppingsserror')}</Alert>
              )}
              {recentCuppingSessions && recentCuppingSessions.length > 0 && (
                <Stack spacing={1}>
                  {recentCuppingSessions.map((session) => {
                    const samples = session.cupping_samples.filter((y) => y.log_data?.inventory_id === beanInfo?.id)
                    return samples.map((sample) => {
                      return <CuppingLinkButton key={`${session.id}-${sample.id}`} session={session} sample={sample} />
                    })
                  })}
                </Stack>
              )}
              {(beanInfo.id === -1 || recentCuppingSessions?.length === 0) && (
                <Alert severity="info">{t('inventory.nocuppings')}</Alert>
              )}
            </Stack>
          </Stack>
          <Stack flex={1}>
            <InventoryForm
              beanInfo={beanInfo}
              isSaving={isSaving}
              onSave={saveInventory}
              onSetArchived={setArchived}
              isSettingArchived={isSettingArchived}
            />
          </Stack>
        </Stack>
      )}
    </Container>
  )
}

export default InventoryDetailView
